import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { pdfColors, pdfFontSize, pdfLetter, pdfStatusTones, type PdfTone } from '../theme';

export interface SummaryItem {
  label: string;
  value: number;
  tone?: PdfTone;
  /** A dot in this exact colour, where the document has its own palette (attendance). */
  color?: string;
}

export interface SummaryGroup {
  /** "By status", "By class". */
  label: string;
  items: SummaryItem[];
}

/** Count rows by a label, most first — one group's items. Rows with no label are left out. */
export function tally<T>(rows: T[], labelOf: (row: T) => string, toneOf?: (row: T) => PdfTone): SummaryItem[] {
  const counts = new Map<string, SummaryItem>();
  for (const row of rows) {
    const label = labelOf(row);
    if (!label) continue;
    const item = counts.get(label) ?? { label, value: 0, tone: toneOf?.(row) };
    item.value += 1;
    counts.set(label, item);
  }
  return Array.from(counts.values()).sort((a, b) => b.value - a.value);
}

const styles = StyleSheet.create({
  wrap: {
    marginBottom: 10,
    paddingBottom: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: pdfColors.divider,
    borderBottomStyle: 'solid',
  },
  line: { flexDirection: 'row', alignItems: 'center', paddingVertical: 2 },
  group: {
    width: 64,
    fontSize: pdfFontSize.xxs,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
    color: pdfColors.textMuted,
  },
  items: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' },
  item: { flexDirection: 'row', alignItems: 'center', marginRight: 14 },
  dot: { width: 5, height: 5, borderRadius: 2.5, marginRight: 4 },
  label: { fontSize: pdfFontSize.xs + 0.5, color: pdfColors.textSecondary },
  value: { fontSize: pdfFontSize.xs + 0.5, fontWeight: 700, color: pdfColors.textPrimary, marginLeft: 3 },
});

/**
 * A quiet summary under a document's header: a line or two of counts —
 * "By status  ● Offered 2  ● Rejected 1" — instead of a block of stat boxes.
 */
const SummaryLine: React.FC<{ groups: SummaryGroup[] }> = ({ groups }) => {
  const shown = groups.filter(g => g.items.length > 0);
  if (!shown.length) return null;
  return (
    <View style={styles.wrap} wrap={false}>
      {shown.map(g => (
        <View key={g.label} style={styles.line}>
          <Text style={styles.group}>{g.label}</Text>
          <View style={styles.items}>
            {g.items.map(it => (
              <View key={it.label} style={styles.item}>
                {(it.color || it.tone) && <View style={[styles.dot, { backgroundColor: it.color ?? pdfStatusTones[it.tone!].border }]} />}
                <Text style={styles.label}>{it.label}</Text>
                <Text style={styles.value}>{it.value}</Text>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
};

export default SummaryLine;
